// src/Map.js
import React, { useState, useEffect, useCallback } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";

// Define libraries outside component to prevent unnecessary re-renders
const libraries = ["places"];

const Map = ({ markers, handleApiLoad, mapRef, selectedMarker }) => {
  const [centerPin, setCenterPin] = useState(undefined);

  // Use the recommended hook for loading Google Maps
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API,
    libraries,
    nonce: window.NONCE_ID, // Optional: only if you use CSP
  });

  // Handle map load in useCallback to avoid re-renders
  const onMapLoad = useCallback(
    (map) => {
      mapRef.current = map;
      if (handleApiLoad) handleApiLoad();
    },
    [mapRef, handleApiLoad]
  );

  // Fit bounds when markers or map reference changes
  useEffect(() => {
    if (mapRef.current && markers.length > 0 && isLoaded) {
      const bounds = new window.google.maps.LatLngBounds();
      let validMarkerFound = false;

      markers.forEach((marker) => {
        if (
          marker.position?.lat !== undefined &&
          marker.position?.lng !== undefined
        ) {
          if (!validMarkerFound) {
            setCenterPin(marker.position);
            validMarkerFound = true;
          }
          bounds.extend(marker.position);
        }
      });

      // Only fit bounds if we have valid markers
      if (validMarkerFound) {
        mapRef.current.fitBounds(bounds);
      }
    }
  }, [markers, mapRef, isLoaded]);

  // Show loading or error state
  if (loadError) {
    return <div className="text-center p-4">Error loading maps</div>;
  }

  if (!isLoaded) {
    return <div className="text-center p-4">Loading maps...</div>;
  }

  return (
    <GoogleMap
      mapContainerStyle={{
        height: "100%",
        width: "100%",
        borderRadius: "10px",
      }}
      center={centerPin}
      zoom={9}
      onLoad={onMapLoad}
    >
      {markers.map((marker) =>
        marker.position?.lat !== undefined &&
        marker.position?.lng !== undefined ? (
          <Marker
            key={marker.id}
            position={marker.position}
            title={marker.title?.address}
          />
        ) : null
      )}

      {selectedMarker && (
        <InfoWindow position={selectedMarker.position}>
          <div className="text-sm">
            <strong>{selectedMarker.title?.shopName}</strong>
            <br />
            {selectedMarker.title?.phoneNumber}
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  );
};

export default Map;
